import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Loader, X, RefreshCw, AlertTriangle, Layers, Package
} from 'lucide-react';
import adminService, { 
  Category, CategoryField, FieldGroup, CreateFieldGroupRequest, 
  UpdateFieldGroupRequest 
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../components/common/DataTable';

const FieldGroupsManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [groups, setGroups] = useState<FieldGroup[]>([]);
  const [fields, setFields] = useState<CategoryField[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modals
  const [addGroupModal, setAddGroupModal] = useState(false);
  const [editGroupModal, setEditGroupModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; group: FieldGroup | null }>({
    show: false,
    group: null
  });
  
  // Forms
  const [addGroupForm, setAddGroupForm] = useState<CreateFieldGroupRequest>({
    category_id: 0,
    group_name: '',
    description: '',
    display_order: 0,
    field_ids: []
  });
  
  const [editGroupForm, setEditGroupForm] = useState<UpdateFieldGroupRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryGroups(selectedCategory);
      fetchCategoryFields(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllCategories();
      setCategories(data.filter(c => c.is_active === 1));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryGroups = async (categoryId: number, showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setGroupsLoading(true);
      }
      
      const data = await adminService.getFieldGroups(categoryId);
      setGroups(data);
      
      if (showToast) {
        toast.success('Groups refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch groups');
    } finally {
      setGroupsLoading(false);
      setRefreshing(false);
    }
  };

  const fetchCategoryFields = async (categoryId: number) => {
    try {
      const data = await adminService.getCategoryFields(categoryId);
      setFields(data);
    } catch (error: any) {
      console.error('Failed to fetch fields:', error);
    }
  };

  const handleAddGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) {
      toast.error('Please select a category first');
      return;
    }

    try {
      setSaving(true);
      await adminService.createFieldGroup({
        ...addGroupForm,
        category_id: selectedCategory
      });
      toast.success('Field group created successfully!');
      setAddGroupModal(false);
      setAddGroupForm({
        category_id: 0,
        group_name: '',
        description: '',
        display_order: 0,
        field_ids: []
      });
      fetchCategoryGroups(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  const handleEditGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editGroupForm || !selectedCategory) return;
    
    try {
      setSaving(true);
      await adminService.updateFieldGroup(editGroupForm);
      toast.success('Field group updated successfully!');
      setEditGroupModal(false);
      setEditGroupForm(null);
      fetchCategoryGroups(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (group: FieldGroup) => {
    // Parse the fields string to get field IDs
    let fieldIds: number[] = [];
    try {
      if (group.fields) {
        const fieldsArray = group.fields.split(',').map(f => {
          const parsed = JSON.parse(f);
          return parsed.field_id;
        });
        fieldIds = fieldsArray;
      }
    } catch (e) {
      console.error('Error parsing fields:', e);
    }

    setEditGroupForm({
      group_id: group.group_id,
      group_name: group.group_name,
      description: group.description || '',
      display_order: group.display_order,
      field_ids: fieldIds
    });
    setEditGroupModal(true);
  };

  const openDeleteModal = (group: FieldGroup) => {
    setDeleteModal({ show: true, group });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, group: null });
  };

  const confirmDelete = async () => {
    if (!deleteModal.group || !selectedCategory) return;
    
    try {
      setDeleting(true);
      await adminService.deleteFieldGroup(deleteModal.group.group_id);
      toast.success('Field group deleted successfully!');
      closeDeleteModal();
      fetchCategoryGroups(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete group');
    } finally {
      setDeleting(false);
    }
  };

  const toggleFieldSelection = (fieldId: number, isAdd: boolean) => {
    if (isAdd) {
      const currentIds = addGroupForm.field_ids || [];
      if (currentIds.includes(fieldId)) {
        setAddGroupForm({
          ...addGroupForm,
          field_ids: currentIds.filter(id => id !== fieldId)
        });
      } else {
        setAddGroupForm({
          ...addGroupForm,
          field_ids: [...currentIds, fieldId]
        });
      }
    } else if (editGroupForm) {
      const currentIds = editGroupForm.field_ids || [];
      if (currentIds.includes(fieldId)) {
        setEditGroupForm({
          ...editGroupForm,
          field_ids: currentIds.filter(id => id !== fieldId)
        });
      } else {
        setEditGroupForm({
          ...editGroupForm,
          field_ids: [...currentIds, fieldId]
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Field Groups Management</h1>
          <p className="text-secondary-600">Organize fields into logical groups for better UX</p>
        </div>
        {selectedCategory && (
          <div className="flex space-x-3">
            <button
              onClick={() => fetchCategoryGroups(selectedCategory, true)}
              disabled={refreshing}
              className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setAddGroupModal(true)}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all hover:scale-105 shadow-green"
            >
              <Plus size={20} />
              <span>Add Group</span>
            </button>
          </div>
        )}
      </div>

      {/* Category Selection */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <label className="block text-sm font-semibold text-secondary-700 mb-3">
          Select Category to Manage Field Groups
        </label>
        <select
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(Number(e.target.value))}
          className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
        >
          <option value="">-- Choose a category --</option>
          {categories.map((cat) => (
            <option key={cat.category_id} value={cat.category_id}>
              {cat.category_name}
            </option>
          ))}
        </select>
      </div>

      {/* Groups List */}
      {selectedCategory && (
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          {groupsLoading ? (
            <div className="text-center py-20">
              <Loader className="animate-spin text-primary-500 mx-auto mb-4" size={48} />
              <p className="text-secondary-600">Loading groups...</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-20">
              <Layers size={64} className="text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-600 font-semibold mb-2">No field groups found</p>
              <p className="text-sm text-secondary-500 mb-6">Organize your fields by creating groups</p>
              <button
                onClick={() => setAddGroupModal(true)}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add First Group</span>
              </button>
            </div>
          ) : (
            <DataTable
              data={groups}
              loading={groupsLoading}
              searchable={true}
              searchPlaceholder="Search groups by name..."
              searchFields={['group_name']}
              defaultPageSize={10}
              emptyMessage="No field groups found"
              emptyIcon={<Layers size={64} className="text-secondary-300 mx-auto mb-4" />}
              columns={[
                {
                  header: 'Group Name',
                  accessor: (group) => (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Layers className="text-primary-600" size={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-secondary-900">{group.group_name}</p>
                      </div>
                    </div>
                  ),
                  sortable: true,
                },
                {
                  header: 'Description',
                  accessor: (group) => (
                    <p className="text-sm text-secondary-600 max-w-md">
                      {group.description || <span className="text-gray-400">No description</span>}
                    </p>
                  ),
                },
                {
                  header: 'Fields Count',
                  accessor: (group) => {
                    let fieldsCount = 0;
                    if (group.fields) {
                      try {
                        const fieldsList = group.fields.split(',').map(f => {
                          try {
                            return JSON.parse(f);
                          } catch {
                            return null;
                          }
                        }).filter(f => f !== null);
                        fieldsCount = fieldsList.length;
                      } catch (e) {
                        fieldsCount = 0;
                      }
                    }
                    return (
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-primary-100 text-primary-700 rounded-lg text-sm font-bold">
                        {fieldsCount}
                      </span>
                    );
                  },
                  align: 'center',
                  sortable: true,
                },
                {
                  header: 'Display Order',
                  accessor: (group) => (
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-bold">
                      {group.display_order}
                    </span>
                  ),
                  align: 'center',
                  sortable: true,
                },
                {
                  header: 'Fields',
                  accessor: (group) => {
                    let fieldsList: any[] = [];
                    if (group.fields) {
                      try {
                        fieldsList = group.fields.split(',').map(f => {
                          try {
                            return JSON.parse(f);
                          } catch {
                            return null;
                          }
                        }).filter(f => f !== null);
                      } catch (e) {
                        fieldsList = [];
                      }
                    }
                    return fieldsList.length > 0 ? (
                      <div className="flex flex-wrap gap-2 max-w-md">
                        {fieldsList.slice(0, 3).map((field: any, idx: number) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center space-x-1 bg-white px-2 py-1 rounded text-xs border border-secondary-200"
                          >
                            <Package size={12} className="text-primary-600" />
                            <span className="font-medium">{field.field_name}</span>
                          </span>
                        ))}
                        {fieldsList.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-secondary-100 text-secondary-600">
                            +{fieldsList.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-secondary-400 text-sm">No fields</span>
                    );
                  },
                },
              ]}
              renderActions={(group) => (
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(group);
                    }}
                    className="bg-warning-50 hover:bg-warning-100 text-warning-600 p-2 rounded-lg transition-colors"
                    title="Edit Group"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(group);
                    }}
                    className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded-lg transition-colors"
                    title="Delete Group"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* Add Group Modal */}
      {addGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-secondary-900">Create Field Group</h2>
              <button onClick={() => setAddGroupModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddGroup} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Group Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={addGroupForm.group_name}
                  onChange={(e) => setAddGroupForm({...addGroupForm, group_name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="e.g., Fuel Pricing"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Description
                </label>
                <textarea
                  value={addGroupForm.description}
                  onChange={(e) => setAddGroupForm({...addGroupForm, description: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Brief description of this group"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={addGroupForm.display_order}
                  onChange={(e) => setAddGroupForm({...addGroupForm, display_order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              {/* Field Selection */}
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-3">
                  Select Fields for this Group
                </label>
                {fields.length === 0 ? (
                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-sm text-warning-700">
                    No fields available. Please create fields first.
                  </div>
                ) : (
                  <div className="border-2 border-secondary-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {fields.map((field) => (
                        <label 
                          key={field.field_id}
                          className="flex items-center space-x-3 p-3 hover:bg-secondary-50 rounded-lg cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={(addGroupForm.field_ids || []).includes(field.field_id)}
                            onChange={() => toggleFieldSelection(field.field_id, true)}
                            className="w-4 h-4 text-primary-600"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">{field.field_name}</p>
                            <p className="text-xs text-secondary-500">{field.field_type}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setAddGroupModal(false)}
                  className="flex-1 px-6 py-3 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      <span>Create Group</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {editGroupModal && editGroupForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-secondary-900">Edit Field Group</h2>
              <button onClick={() => setEditGroupModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleEditGroup} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Group Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editGroupForm.group_name}
                  onChange={(e) => setEditGroupForm({...editGroupForm, group_name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editGroupForm.description}
                  onChange={(e) => setEditGroupForm({...editGroupForm, description: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="Brief description of this group"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={editGroupForm.display_order}
                  onChange={(e) => setEditGroupForm({...editGroupForm, display_order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              {/* Field Selection */}
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-3">
                  Select Fields for this Group
                </label>
                <div className="border-2 border-secondary-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {fields.map((field) => (
                      <label 
                        key={field.field_id}
                        className="flex items-center space-x-3 p-3 hover:bg-secondary-50 rounded-lg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(editGroupForm.field_ids || []).includes(field.field_id)}
                          onChange={() => toggleFieldSelection(field.field_id, false)}
                          className="w-4 h-4 text-primary-600"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-secondary-900">{field.field_name}</p>
                          <p className="text-xs text-secondary-500">{field.field_type}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditGroupModal(false)}
                  className="flex-1 px-6 py-3 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Edit size={20} />
                      <span>Update Group</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.group && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <AlertTriangle className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Delete Group</h2>
              </div>
              <button onClick={closeDeleteModal} disabled={deleting} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to delete <strong>{deleteModal.group.group_name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    <span>Delete Group</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldGroupsManagement;

